import {
  getQuickPicks,
  searchSensorNames,
  isNameTaken,
  nextAvailableName
} from '../../util/sensor-name-suggestions'
import { pipeStrapNamePreset } from '../../util/selective-pipe-strap'

const MAX_NAME_LENGTH = 50

export class StepStartNameSensor {
  constructor (onboarding) {
    this.onboarding = onboarding;
  }

  id = "start_name";
  title = "Name Your Temp Stick";

  // Currently highlighted autocomplete row (-1 = none), for keyboard nav.
  activeSuggestion = -1;
  suggestions = [];

  template = `
  <div class="onboarding-step" data-for="start_name">
    <div class="onboarding-guide onboarding-instruction">

      <div class="onboarding-instruction__view" data-view="main">

        <p class="onboarding-guide-instruction">
          This is the name you'll see on your dashboard and in every alert
        </p>

        <div class="onboarding-name-picker">

          <div class="onboarding-name-picker__label" data-for="presets_label">
            Popular names
          </div>

          <div class="onboarding-name-options" data-for="name_presets"></div>

          <div class="onboarding-name-picker__field">
            <label for="onboarding_sensor_name">Or type your own</label>
            <input
              type="text"
              id="onboarding_sensor_name"
              class="onboarding-name-picker__input"
              placeholder="Start typing a name..."
              maxlength="${MAX_NAME_LENGTH}"
              autocomplete="off"
              autocapitalize="none"
              autocorrect="off"
              spellcheck="false"
              aria-autocomplete="list"
            />
            <div class="onboarding-name-picker__dropdown" data-for="name_suggestions"></div>
          </div>

          <div class="onboarding-name-picker__note" data-for="name_note"></div>

        </div>

        <div class="onboarding-instruction__actions">
          <button type="button" class="button onboarding-guide-next-btn" data-action="name_continue" disabled>
            Continue
          </button>
          <button type="button" class="onboarding-guide-help-btn onboarding-instruction__help-link" data-action="help-toggle">
            Help me pick a good name
          </button>
        </div>

      </div>

      <div class="onboarding-instruction__view" data-view="help">

        <h2 class="onboarding-guide-heading onboarding-instruction__title">
          What makes a good sensor name
        </h2>

        <div class="onboarding-selective-questions-help">
          <div class="onboarding-selective-questions-help__item">
            <div class="onboarding-selective-questions-help__heading">Name the spot, not the sensor</div>
            <div class="onboarding-selective-questions-help__text">&ldquo;Garage Freezer&rdquo; tells you where to go. &ldquo;Temp Stick 2&rdquo; doesn't. Alerts land on your phone at the worst possible moment &mdash; the name should tell you where to walk.</div>
          </div>
          <div class="onboarding-selective-questions-help__item">
            <div class="onboarding-selective-questions-help__heading">Add a qualifier if you'll have more than one</div>
            <div class="onboarding-selective-questions-help__text">Two freezers become &ldquo;Kitchen Freezer&rdquo; and &ldquo;Garage Freezer&rdquo;. Several buildings become &ldquo;Warehouse Cooler&rdquo; and &ldquo;Shop Cooler&rdquo;.</div>
          </div>
          <div class="onboarding-selective-questions-help__item">
            <div class="onboarding-selective-questions-help__heading">Keep it short</div>
            <div class="onboarding-selective-questions-help__text">Two or three words reads cleanly in alert texts, emails, and reports. Long names get cut off.</div>
          </div>
          <div class="onboarding-selective-questions-help__item">
            <div class="onboarding-selective-questions-help__heading">You can change it later</div>
            <div class="onboarding-selective-questions-help__text">Nothing here is permanent &mdash; rename the sensor any time from its settings page.</div>
          </div>
        </div>

        <div class="onboarding-instruction__actions">
          <button type="button" class="button onboarding-guide-next-btn" data-action="help_back">
            Got it
          </button>
        </div>

      </div>

    </div>
  </div>
  `;

  onBack = () => {

    // Help sub-view: back returns to the picker rather than leaving the step.
    if (this.$helpView && this.$helpView.hasClass('is-active')) {
      this.showMainView();
      return;
    }

    this.onboarding.goToStep(this.onboarding.stepBeforeNameStep());

  }

  onActivate = () => {

    this.onboarding.showBackButton();

    this.showMainView();
    this.renderPresets();

    // Re-entering the step (e.g. back from wifi select) keeps whatever name was
    // already chosen so the user doesn't have to pick again. First entry starts
    // from the pipe-placement answer when there is one — the user already said
    // where this sensor is going, so that name arrives pre-selected.
    this.$nameField.val(
      this.onboarding.sensorName || pipeStrapNamePreset(this.onboarding.pipe_strap_location)
    );
    this.hideSuggestions();
    this.syncState();

  }

  // ────────────────────────────────────────────────────────────────────────────
  // Views
  // ────────────────────────────────────────────────────────────────────────────

  showMainView = () => {
    if (!this.$mainView || !this.$helpView) return;
    this.$mainView.addClass('is-active');
    this.$helpView.removeClass('is-active');
  }

  showHelpView = () => {
    this.hideSuggestions();
    this.$mainView.removeClass('is-active');
    this.$helpView.addClass('is-active');
    this.onboarding.showBackButton();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Data helpers
  // ────────────────────────────────────────────────────────────────────────────

  isSelective = () => {
    return typeof this.onboarding.isSelective === 'function'
      ? this.onboarding.isSelective()
      : false;
  }

  getExistingNames = () => {
    return Array.isArray(this.onboarding.existing_sensor_names)
      ? this.onboarding.existing_sensor_names
      : [];
  }

  getTypedName = () => {
    return (this.$nameField.val() || '').trim();
  }

  escapeHtml = value => {
    return String(value == null ? '' : value).replace(/[&<>"']/g, char => {
      switch (char) {
        case '&': return '&amp;'
        case '<': return '&lt;'
        case '>': return '&gt;'
        case '"': return '&quot;'
        case '\'': return '&#39;'
        default: return char
      }
    });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Presets
  // ────────────────────────────────────────────────────────────────────────────

  renderPresets = () => {

    const isSelective = this.isSelective();
    const picks = getQuickPicks(isSelective);

    this.$presetsLabel.text(isSelective ? 'Common sensor names' : 'Popular names');

    this.$presets.html(picks.map(name => `
      <button type="button" class="button name-preset-btn" data-name="${this.escapeHtml(name)}">
        ${this.escapeHtml(name)}
      </button>
    `).join(''));

  }

  selectPreset = name => {
    this.$nameField.val(name);
    this.hideSuggestions();
    this.syncState();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Autocomplete
  // ────────────────────────────────────────────────────────────────────────────

  renderSuggestions = () => {

    const query = this.getTypedName();

    // Nothing typed yet: the preset buttons above are already the "what should
    // I pick" answer, so don't bury them under a dropdown.
    if (query.length === 0) {
      this.hideSuggestions();
      return;
    }

    const existing = this.getExistingNames();

    this.suggestions = searchSensorNames(query, {
      isSelective: this.isSelective(),
      limit: 8,
      extra: existing
    });

    // An exact match adds nothing — the field already says it.
    if (
      this.suggestions.length === 1 &&
      this.suggestions[0].toLowerCase() === query.toLowerCase()
    ) {
      this.hideSuggestions();
      return;
    }

    if (this.suggestions.length === 0) {
      this.hideSuggestions();
      return;
    }

    this.activeSuggestion = -1;

    this.$suggestions.html(this.suggestions.map((name, index) => `
      <button
        type="button"
        class="onboarding-name-picker__suggestion"
        data-name="${this.escapeHtml(name)}"
        data-index="${index}"
      >
        <span class="onboarding-name-picker__suggestion-label">${this.escapeHtml(name)}</span>
        ${isNameTaken(name, existing)
          ? '<span class="onboarding-name-picker__suggestion-tag">in use</span>'
          : ''}
      </button>
    `).join(''));

    this.$suggestions.addClass('is-active');

  }

  hideSuggestions = () => {
    this.suggestions = [];
    this.activeSuggestion = -1;

    if (this.$suggestions) {
      this.$suggestions.removeClass('is-active').empty();
    }
  }

  moveActiveSuggestion = direction => {

    if (!this.$suggestions.hasClass('is-active') || this.suggestions.length === 0) {
      return;
    }

    const count = this.suggestions.length;

    // -1 means "nothing highlighted". The list wraps through that slot so
    // arrowing past either end hands the user their own typing back.
    let next = this.activeSuggestion + direction;

    if (next >= count) {
      next = -1;
    } else if (next < -1) {
      next = count - 1;
    }

    this.activeSuggestion = next;

    this.$suggestions.find('.onboarding-name-picker__suggestion').each((i, el) => {
      const isActive = i === this.activeSuggestion;

      $(el).toggleClass('is-active', isActive);

      // The dropdown scrolls once the list is longer than it is tall.
      if (isActive && el.scrollIntoView) {
        el.scrollIntoView({ block: 'nearest' });
      }
    });

  }

  // ────────────────────────────────────────────────────────────────────────────
  // Duplicate-name nudge
  // ────────────────────────────────────────────────────────────────────────────

  syncState = () => {

    const name = this.getTypedName();
    const existing = this.getExistingNames();

    // Highlight whichever preset matches the current value.
    this.$presets.find('.name-preset-btn').each((i, el) => {
      const $btn = $(el);
      $btn.toggleClass(
        'is-selected',
        String($btn.attr('data-name') || '').toLowerCase() === name.toLowerCase()
      );
    });

    this.$continueBtn.prop('disabled', name.length === 0);

    if (name.length > 0 && isNameTaken(name, existing)) {
      const suggested = nextAvailableName(name, existing);

      this.$note.addClass('is-active').html(`
        <span class="onboarding-name-picker__note-text">
          You already have a sensor named &ldquo;${this.escapeHtml(name)}&rdquo;.
        </span>
        <button type="button" class="onboarding-name-picker__note-action" data-name="${this.escapeHtml(suggested)}">
          Use &ldquo;${this.escapeHtml(suggested)}&rdquo; instead
        </button>
      `);
    } else {
      this.$note.removeClass('is-active').empty();
    }

  }

  // ────────────────────────────────────────────────────────────────────────────
  // Advance
  // ────────────────────────────────────────────────────────────────────────────

  proceedWithName = name => {

    const trimmed = (name || '').trim();

    if (!trimmed) {
      this.$nameField.trigger('focus');
      return;
    }

    this.onboarding.sensorName = trimmed.slice(0, MAX_NAME_LENGTH);

    this.hideSuggestions();
    this.onboarding.goToStep('connected_wifi_select');

  }

  bindActions = () => {

    const $step = $(this.onboarding.container).find('.onboarding-step[data-for="start_name"]');

    this.$mainView     = $step.find('[data-view="main"]');
    this.$helpView     = $step.find('[data-view="help"]');
    this.$presetsLabel = $step.find('[data-for="presets_label"]');
    this.$presets      = $step.find('[data-for="name_presets"]');
    this.$nameField    = $step.find('.onboarding-name-picker__input');
    this.$suggestions  = $step.find('[data-for="name_suggestions"]');
    this.$note         = $step.find('[data-for="name_note"]');
    this.$continueBtn  = $step.find('[data-action="name_continue"]');
    this.$helpToggle   = $step.find('[data-action="help-toggle"]');
    this.$helpBack     = $step.find('[data-action="help_back"]');

    this.showMainView();

    this.$presets.on('click', '.name-preset-btn', e => {
      this.selectPreset(String($(e.currentTarget).attr('data-name') || ''));
    });

    // mousedown (not click) so the pick lands before the field's blur fires and
    // tears the dropdown down underneath the pointer.
    this.$suggestions.on('mousedown', '.onboarding-name-picker__suggestion', e => {
      e.preventDefault();
      this.selectPreset(String($(e.currentTarget).attr('data-name') || ''));
      this.$nameField.trigger('focus');
    });

    this.$note.on('click', '.onboarding-name-picker__note-action', e => {
      this.selectPreset(String($(e.currentTarget).attr('data-name') || ''));
    });

    this.$nameField.on('input', () => {
      this.renderSuggestions();
      this.syncState();
    });

    this.$nameField.on('focus', () => {
      this.renderSuggestions();
    });

    this.$nameField.on('blur', () => {
      // Give the mousedown handler above a chance to run first.
      setTimeout(() => this.hideSuggestions(), 120);
    });

    this.$nameField.on('keydown', e => {

      switch (e.key) {

        case 'ArrowDown':
          e.preventDefault();
          this.moveActiveSuggestion(1);
          break;

        case 'ArrowUp':
          e.preventDefault();
          this.moveActiveSuggestion(-1);
          break;

        case 'Escape':
          this.hideSuggestions();
          break;

        case 'Enter':
          e.preventDefault();

          if (this.activeSuggestion > -1 && this.suggestions[this.activeSuggestion]) {
            this.selectPreset(this.suggestions[this.activeSuggestion]);
            return;
          }

          this.proceedWithName(this.getTypedName());
          break;

      }

    });

    this.$continueBtn.on('click', () => {
      this.proceedWithName(this.getTypedName());
    });

    this.$helpToggle.on('click', () => {
      this.showHelpView();
    });

    // Return to the picker without touching the field — the name they were
    // part-way through typing is still there.
    this.$helpBack.on('click', () => {
      this.showMainView();
    });

  }

}
